import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, HostListener, OnDestroy } from '@angular/core';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { Subscription, combineLatest, timer } from 'rxjs';
import { filter, take, timeout, switchMap } from 'rxjs/operators';
import { AuthService } from '../../guards/service/auth.service';
import { FreelancerDataService } from '../../../services/freelancer-data.service';
import { ToastrService } from 'ngx-toastr';
import { DomainService } from '../../domain/domain.service';
import { AvailabilityGaugeComponent } from '../availability-gauge/availability-gauge.component';
import { CommunityService } from '../../../services/community.service';

@Component({
  selector: 'app-top-nav',
  standalone: true,
  imports: [CommonModule, RouterModule, AvailabilityGaugeComponent],
  templateUrl: './top-nav.component.html',
  styleUrls: ['./top-nav.component.css']
})
export class TopNavComponent implements OnInit, OnDestroy {
  spaceID: number | undefined;
  profileDetails: any;
  profilePhoto: string = 'https://kontact-app-public.s3-ap-southeast-1.amazonaws.com/images/Sign-Up-Image.jpg';

  @Input() sidebarOpen: boolean = false;
  @Output() toggleSidebar = new EventEmitter<void>();

  isMobile = false;
  currentSlug = '';
  currentCommunityId = '';
  availabilityUpdatedAt: string | null = null;
  availabilityTooltip = 'Availability status';
  showAvailabilityPrompt = false;
  IsRecruiter: boolean = false;

  private subs = new Subscription();

  // dedupe guards
  private lastInitKey = '';
  private inFlight = {
    avatar: false,
    personal: false,
    availability: false,
    recruiter: false,
  };
  private destroyed = false;
  private lastAvailabilityKey = '';
  isKARecruiter:  boolean = false;

  constructor(
    private authService: AuthService,
    private dataService: FreelancerDataService,
    private toastrService: ToastrService,
    public domainService: DomainService,
    private router: Router,
    private communityService: CommunityService,
  ) {}

  ngOnInit(): void {
    this.isMobile = window.innerWidth < 768;

    // Re-init when domain context + space are ready
    this.subs.add(
      combineLatest([this.domainService.ctxReady$, this.domainService.spaceID$])
        .pipe(filter(([ready, sid]) => !!ready && !!sid))
        .subscribe(() => this.initWithContext())
    );

    // Recompute availability on page changes (but only after init)
    this.subs.add(
      this.router.events
        .pipe(filter(e => e instanceof NavigationEnd))
        .subscribe(() => this.refreshAvailabilityStatus())
    );

    if (this.domainService.isContextReadySync()) this.initWithContext();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.subs.unsubscribe();
  }

  @HostListener('window:resize') onResize() {
    this.isMobile = window.innerWidth < 768;
  }

  private initWithContext(): void {
    this.spaceID = this.domainService.getSpaceIdSync() || 0;
    this.currentSlug = this.domainService.getSlugSync() || '';
    this.currentCommunityId = String(this.domainService.getCommunityIdSync() || '');

    const key = `${this.spaceID}|${this.currentSlug}|${this.currentCommunityId}`;
    if (key === this.lastInitKey) return;
    this.lastInitKey = key;

    if (!this.spaceID || !this.authService.isLoggedIn()) return;

    // ⏳ Gate all data loads until token’s space matches the target SpaceID
    this.waitForTokenSpaceMatch(this.spaceID, 2000).then((ok) => {
      if (!ok) {
        // Even if mismatch persists, don’t spam requests; show prompt-ish state
        this.showAvailabilityPrompt = true;
        this.availabilityTooltip = 'Switching community...';
        return;
      }
      this.getFreelancerAvatar();
      this.getFreelancerPersonalInfo();
      this.refreshAvailabilityStatus();
    });
  }

  /** Waits until JWT’s space_id equals target SpaceID (or times out). */
  private waitForTokenSpaceMatch(target: number, ms: number): Promise<boolean> {
    const matches = () => (this.authService.getSpaceIdFromToken() || 0) === target;
    if (matches()) return Promise.resolve(true);

    return new Promise<boolean>((resolve) => {
      const sub = this.authService.authChanged$.subscribe(() => {
        if (matches()) {
          sub.unsubscribe();
          resolve(true);
        }
      });
      // safety timeout
      timer(ms).pipe(take(1)).subscribe(() => {
        try { sub.unsubscribe(); } catch {}
        resolve(matches());
      });
    });
  }

  getFreelancerPersonalInfo(): void {
    if (!this.spaceID) return;
    if (this.inFlight.personal) return;
    this.inFlight.personal = true;

    this.dataService.getFreelancerProfile({}, this.spaceID).subscribe({
      next: (res) => {
        this.profileDetails = res.data;
        this.inFlight.personal = false;

        if (this.domainService.isCommunityDomain()) {
          this.getRecruiterStatus();
        }
      },
      error: () => {
        this.inFlight.personal = false;
      }
    });
  }

  getFreelancerAvatar(): void {
    if (!this.spaceID) return;
    if (this.inFlight.avatar) return;
    this.inFlight.avatar = true;

    this.dataService.getFreelancerAvatar({}, this.spaceID).subscribe({
      next: (res) => {
        const url = this.pickAvatarUrl(res);
        if (url) this.profilePhoto = url;
        this.inFlight.avatar = false;
      },
      error: () => {
        this.inFlight.avatar = false;
      }
    });
  }

  private refreshAvailabilityStatus(): void {
    const space = this.spaceID || 0;
    const key = `${space}`;
    if (this.inFlight.availability || key === this.lastAvailabilityKey) return;

    this.lastAvailabilityKey = key;
    this.inFlight.availability = true;

    this.showAvailabilityPrompt = false;
    this.availabilityUpdatedAt = null;

    if (!space) {
      this.showAvailabilityPrompt = true;
      this.availabilityTooltip = 'Availability status unknown';
      this.inFlight.availability = false;
      return;
    }

    // Also guard on token-space match to avoid 401 noise during swaps
    this.waitForTokenSpaceMatch(space, 1500).then(() => {
      this.dataService.getUserAvailabilty({}, space).subscribe({
        next: (res) => {
          const availability = res?.data?.availability ?? null;
          const updatedAt: string | null = availability?.updatedAt ?? null;

          if (updatedAt) {
            this.availabilityUpdatedAt = updatedAt;
            const days = Math.max(
              0,
              Math.floor((Date.now() - new Date(updatedAt).getTime()) / 86_400_000)
            );
            this.availabilityTooltip = `Last updated ${days} day(s) ago`;
            this.showAvailabilityPrompt = false;
          } else {
            this.availabilityTooltip = 'No availability on file';
            this.showAvailabilityPrompt = true;
          }
          this.inFlight.availability = false;
        },
        error: () => {
          this.showAvailabilityPrompt = true;
          this.availabilityUpdatedAt = null;
          this.availabilityTooltip = 'Availability status unknown';
          this.inFlight.availability = false;
        }
      });
    });
  }

  private getCommunityContext(): { slug: string | null; cid: string | null } {
    const slug = this.domainService.getSlugSync();
    const cid = this.domainService.getCommunityIdSync();
    return { slug, cid: cid ? String(cid) : null };
  }

  goToAvailability(): void {
    const { slug, cid } = this.getCommunityContext();
    const status = this.computeTraffic(this.availabilityUpdatedAt);
    const returnUrl = this.router.url;

    if (slug && cid) {
      this.router.navigate(['/', slug, cid, 'profile'], { queryParams: { tab: 'availability', status, returnUrl } });
    } else {
      this.router.navigate(['/profile'], { queryParams: { tab: 'availability', status, returnUrl, source: 'topnav' } });
    }
  }

  private computeTraffic(updatedAt: string | null): 'green'|'yellow'|'red'|'unknown' {
    if (!updatedAt) return 'unknown';
    const days = Math.max(0, Math.floor((Date.now() - new Date(updatedAt).getTime()) / 86_400_000));
    if (days <= 7) return 'green';
    if (days <= 14) return 'yellow';
    return 'red';
  }

  private getRecruiterStatus() {
    if (!this.profileDetails || !this.spaceID || !this.currentCommunityId) return;
    if (this.inFlight.recruiter) return;
    this.inFlight.recruiter = true;

    const data = {
      user_id: this.profileDetails.user_id,
      space_id: this.spaceID,
      community_id: this.currentCommunityId
    };

    this.communityService.getRecruiterStatus(data).subscribe({
      next: (res) => {
        this.IsRecruiter = !!res?.data?.is_recruiter;
        this.isKARecruiter = res.data.is_ka_recruiter;
        if (this.IsRecruiter) this.refreshAvailabilityStatus();
        this.inFlight.recruiter = false;
      },
      error: () => {
        this.inFlight.recruiter = false;
      },
    });
  }

  onLogout(): void {
    this.authService.handleLogout();
  }

  // --- helpers for avatar shape ---
  private pickAvatarUrl(res: any): string | null {
    const candidates: any[] = [
      res,
      res?.data,
      res?.data?.url,
      res?.data?.avatar,
      res?.url,
      res?.avatar,
      Array.isArray(res?.data) ? res.data[0] : null,
    ].filter(Boolean);

    for (const c of candidates) {
      if (typeof c === 'string' && c.trim()) return c.trim();
      if (typeof c === 'object') {
        const maybe = (c as any)?.url ?? (c as any)?.avatar ?? (Array.isArray(c) ? c[0] : null);
        if (typeof maybe === 'string' && maybe.trim()) return maybe.trim();
      }
    }
    return null;
  }
}
